import { inject, Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, merge, of, switchMap } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { filterBoolean, Helpers } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { LocationSelectors } from '@core/locations-data';
import { LocationsHelperService } from '@core/locations-legacy';
import { JobItemsSelectors, JobsActions } from '@core/offers-data';
import type { OfferItem } from '@core/offers-types';
import {
  EmploymentType,
  JobEditorDialogTab,
  OfferStatus,
  StatusChangeType,
} from '@core/offers-types';
import type { DialogConfirmOptions } from '@core/user-interface-legacy';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';
import { injectSlotsLimitDialogOpenerContract } from '@recruitee/billing-contracts';
import { SubscriptionService } from '@recruitee/company-logic';
import { CustomFieldsHelperService } from '@recruitee/custom-fields-util';
import type { JobEditorNavigationExtraState } from '@recruitee/job-editors-shared-types';
import type { JobValidationErrors } from '@recruitee/offers-legacy';
import { JobSchedulerFacade, JobValidationFormErrorType } from '@recruitee/offers-legacy';
import { JobPublishedDialogFacade, StatusChangeTexts } from '@recruitee/offers-logic';
import type { StatusChangeFacade, StatusChangeParam } from '@recruitee/offers-logic';
import { isRequisitionRequired$ } from '@recruitee/offers-util';

import { JobsStatusAnalyticsHelperService } from '../analytics/jobs-status-analytics.helper';

@Injectable()
export class StatusChangeDefaultFacade implements StatusChangeFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly subscription = inject(SubscriptionService);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly texts = inject(StatusChangeTexts);
  private readonly router = inject(Router);
  private readonly jobsStatusAnalytics = inject(JobsStatusAnalyticsHelperService, {
    optional: true,
  });
  private readonly locationsHelper = inject(LocationsHelperService);
  private readonly jobPublishedDialogFacade = inject(JobPublishedDialogFacade);
  private readonly customFieldsHelper = inject(CustomFieldsHelperService);
  private readonly slotsLimitDialogOpener = injectSlotsLimitDialogOpenerContract();
  protected readonly dialog = inject(DialogService);
  protected readonly vcRef = inject(ViewContainerRef);
  protected readonly jobSchedulerFacade = inject(JobSchedulerFacade);
  protected readonly i18n = inject(I18nService);

  public setClosed(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Closed)) return;
    this.confirmClose$(param).subscribe(() => this.execute(param, StatusChangeType.Close));
  }

  public setInternal(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Internal)) return;
    this.verifyIssues(param.id, OfferStatus.Internal)
      .pipe(filter(validationErrors => !validationErrors))
      .subscribe(() => this.execute(param, StatusChangeType.Unpublish));
  }

  public setPublished(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Published)) return;
    this.verifyIssues(param.id, OfferStatus.Published)
      .pipe(filter(validationErrors => !validationErrors))
      .subscribe(() => this.execute(param, StatusChangeType.Publish));
  }

  public confirmClose$(param: StatusChangeParam): Observable<boolean> {
    const dialogOptions =
      param.status === OfferStatus.Archived
        ? { ...this.texts.model.retrieve }
        : { ...this.texts.model.close };

    if (this.jobSchedulerFacade.hasScheduledDates(param.jobScheduler)) {
      dialogOptions.message = this.jobSchedulerFacade.getCloseConfirmDialogMessage(
        param.jobScheduler,
      );
    }

    return this.openConfirmDialog(dialogOptions).pipe(filterBoolean);
  }

  private execute(param: StatusChangeParam, name: StatusChangeType) {
    this.setupEffects(param, name);
    this.store.dispatch(
      JobsActions.changeStatus({
        payload: { id: param.id, status: name, currentStatus: param.status },
      }),
    );
  }

  private setupEffects(param: StatusChangeParam, action: StatusChangeType) {
    merge(
      this.actions$.pipe(
        ofType(JobsActions.changeStatusSuccess),
        tap(() => this.success(action, param.id)),
      ),
      this.actions$.pipe(
        ofType(JobsActions.changeStatusFailed),
        tap(({ payload }) => this.failed(payload.error)),
      ),
    )
      .pipe(take(1))
      .subscribe();
  }

  private success(name: StatusChangeType, jobId: number) {
    if ([StatusChangeType.Publish, StatusChangeType.Unpublish].includes(name)) {
      this.jobPublishedDialogFacade.open(jobId);
    } else {
      this.notificationsCtrl.success(
        this.i18n.translate(`app.offers.actions.${name}.success_message.job`),
      );
    }

    if (name === StatusChangeType.Publish || name === StatusChangeType.Unpublish) {
      const newStatus =
        name === StatusChangeType.Publish ? OfferStatus.Published : OfferStatus.Internal;
      this.jobsStatusAnalytics?.trackJobStatusChange(jobId, newStatus);
    }
    if (name === StatusChangeType.Close) {
      this.jobsStatusAnalytics?.trackJobClose(jobId);
    }
  }

  private failed(error: { errorFields: any }) {
    let slotsLimitExceeded = false;
    const errorKeys = Object.keys(error.errorFields);
    const errors = errorKeys.map(errorKey => {
      const translationKey = Helpers.underscore(errorKey);
      if (errorKey === 'slotsLimitExceeded') {
        slotsLimitExceeded = true;

        return {
          key: errorKey,
          text: '',
        };
      }
      return {
        key: errorKey,
        text: this.i18n.translate(`app.offer_form.errors.${translationKey}`),
      };
    });

    if (slotsLimitExceeded && this.subscription.isTrial()) {
      this.slotsLimitDialogOpener.open({ width: DialogWidth.Small }, { isTrial: true });
    } else {
      this.notificationsCtrl.error({
        title: this.i18n.translate('app.common.error_something_went_wrong'),
        messageHTML: `<ul>${errors.map(errorField => `<li>${errorField.text}</li>`).join('')}</ul>`,
      });
    }
  }

  private openConfirmDialog(options: DialogConfirmOptions): Observable<boolean> {
    return this.dialog.information({ ...options, isCancelable: true }).pipe(filterBoolean);
  }

  private verifySubscription(param: StatusChangeParam, nextStatus: OfferStatus): boolean {
    const isActionAllowed = this.subscription.isStatusChangeAllowed(param.status, nextStatus);

    if (!isActionAllowed) {
      this.openSlotsLimitDialog();
      return false;
    }

    return true;
  }

  private verifyIssues(jobId: number, nextStatus: OfferStatus): Observable<JobValidationErrors> {
    let job: OfferItem;
    let isRequisitionRequired: boolean;
    let skipValidation = false;

    return combineLatest({
      job: this.store.select(JobItemsSelectors.byId(jobId)).pipe(filterBoolean),
      isRequisitionRequired: isRequisitionRequired$(this.store),
    }).pipe(
      tap(data => {
        job = data.job;
        isRequisitionRequired = data.isRequisitionRequired;
        skipValidation =
          job.status === OfferStatus.Published && nextStatus !== OfferStatus.Published;
      }),
      switchMap(data =>
        combineLatest([
          this.locationsErrors(data),
          this.isEmploymentTypeInvalid(data),
          this.customFieldsErrors(data),
        ]),
      ),
      map(
        ([
          { isActiveValidLocationMissing, exceededLocationsLimit, isActiveLocationMissing },
          invalidEmploymentType,
          { exceededCustomFieldsLimit },
        ]) => {
          if (!job.issues) return null;
          let validationErrors: JobValidationErrors = skipValidation
            ? null
            : {
                formErrorType: job.issues.isRequiredDataMissing
                  ? JobValidationFormErrorType.MissingRequiredFields
                  : null,
                isRequisitionMissing: isRequisitionRequired
                  ? job.issues.isRequisitionMissing
                  : false,
                isApprovedRequisitionMissing: isRequisitionRequired
                  ? job.issues.isRequisitionMissing
                  : false,
                invalidEmploymentType,
                exceededLocationsLimit,
                isActiveValidLocationMissing,
                isActiveLocationMissing,
                exceededCustomFieldsLimit,
              };
          if (validationErrors && Object.values(validationErrors).every(val => !val)) {
            validationErrors = null;
          }
          return { validationErrors, jobId: job.id };
        },
      ),
      tap(result => {
        if (!result) return;
        this.validationFailedEffect(result.validationErrors, result.jobId);
      }),
      map(result => (result ? result.validationErrors : null)),
      take(1),
    );
  }

  private validationFailedEffect(validationErrors: JobValidationErrors, jobId: number) {
    if (!validationErrors) return;
    const viewUrl = { outlets: { 'job-editor': [jobId, JobEditorDialogTab.JobDetails] } };
    const toastRef = this.notificationsCtrl.error({
      title: this.i18n.translate('app.offers.notifications.generic_form_error_title'),
      message: this.i18n.translate('app.offers.notifications.generic_form_error_message'),
      topButtonLabel: this.i18n.translate('app.offers.view'),
    });

    toastRef
      .topButtonClick()
      .pipe(take(1), takeUntil(toastRef.afterClosed))
      .subscribe(() => {
        const state: JobEditorNavigationExtraState = {
          validationFailed: true,
        };
        this.router.navigate([viewUrl], {
          state,
        });
      });
  }

  private openSlotsLimitDialog() {
    this.slotsLimitDialogOpener.open(
      { width: DialogWidth.Small },
      { isTrial: this.subscription.isTrial() },
    );
  }

  private locationsErrors(data: { job: OfferItem }) {
    return combineLatest([
      this.store.select(LocationSelectors.byIds(data.job.locationIds || [])),
      this.locationsHelper.getLocationsLimit(),
    ]).pipe(
      take(1),
      switchMap(([locations, locationsLimit]) => {
        const exceededLocationsLimit = locations.length > locationsLimit;
        const isActiveLocationMissing = !locations.some(location => !location.archivedAt);
        const isActiveValidLocationMissing = !locations.some(
          location => !location.archivedAt && location.isValid,
        );

        return of({
          isActiveLocationMissing,
          isActiveValidLocationMissing,
          exceededLocationsLimit,
        });
      }),
    );
  }

  private customFieldsErrors(data: { job: OfferItem }) {
    const exceededCustomFieldsLimit = this.customFieldsHelper.getIsCustomFieldsLimitExceeded(
      data.job.dynamicFieldsCount ?? 0,
    );
    return of({ exceededCustomFieldsLimit });
  }

  private isEmploymentTypeInvalid(data: { job: OfferItem }): Observable<boolean> {
    const employmentType = data.job.employmentType;
    const isInvalid = [EmploymentType.FullTime, EmploymentType.PartTime].includes(
      <EmploymentType>employmentType,
    );
    return of(isInvalid);
  }
}
